import {
  FormControlLabel,
  Switch,
  TextField,
  Grid,
  Button,
  FormHelperText,
  IconButton,
  Box
} from '@mui/material';

import {useCallback, useMemo, useState} from 'react';

import PhotoCamera from '@mui/icons-material/PhotoCamera';

import {Formik, Form} from 'formik';

import GroupValidationSchema from '@/schemas/groups';

import {useTranslation} from 'react-i18next';

import {TITLE_MAX_LENGTH, DESC_MAX_LENGTH} from '@/schemas/groups';
import {useAppSelector} from '@/redux-store/store';
import {GroupData, GroupFormData} from '@/types/group';

import {updateGroup} from '@/rest/groups/update-group';

import {LocationInput} from '@/components/forms/input/location';
import {GroupCreateFormState} from '@/redux-store/slices/groups';
import {SportSelectInput} from '@/components/forms/input/sports';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '@/constants';
import CachedIcon from '@mui/icons-material/Cached';
import {env} from '@/config';
import TypographyTitle from '@/components/ui/typography-title';
import {decodeBase64ToObject, getAppTag, isAllowedTag} from '@/utils';
import {CompanyInput} from '@/components/forms/input/company';
import ResponsiveTypography from '@/components/ui/responsive-typography';

const {CONTENT_SERVER, TAG_PAIRS} = env;
const groupsImage = `${CONTENT_SERVER}assets/sports/team-new.jpg`;

interface GroupEditFormProps {
  onCancel?: () => void;
  onSubmit?: (editedTeam: GroupFormData) => void;
  onError?: (error: Error) => void;
}

const GroupEditForm: React.FC<
  GroupEditFormProps & {initialValues: GroupData}
> = ({onCancel, onSubmit, onError, initialValues}) => {
  const {APP_VARIANT} = env;
  const {t} = useTranslation();

  const groupEditSchema = GroupValidationSchema(t);

  const userValues = useAppSelector((state) => state.userInfos.userInfos);
  const sportsFromRedux = useAppSelector((state) => state.sports);
  const {currentCompanyId} = useAppSelector((state) => state.auth);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);

  const [titleLength, setTitleLength] = useState(initialValues.title.length);
  const [descriptionLength, setDescriptionLength] = useState(
    initialValues.description.length
  );
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [selectedSports, setSelectedSports] = useState<string[]>(
    initialValues.sports.map((sport) => sport._id)
  );

  const availableCompanies = userValues.companies;
  const trainerInSjOne = userRole === 'TRAINER' && APP_VARIANT === 'sjone';

  const tagPairs = decodeBase64ToObject(TAG_PAIRS);

  const tag = getAppTag(userValues.email || '', tagPairs);
  const allowedTag = tag && isAllowedTag(tag, TAG_PAIRS) ? tag : undefined;

  const companyId =
    APP_VARIANT === 'sjone' &&
    ((userValues &&
      userValues.companies &&
      userValues.companies.length === 1 &&
      userValues.companies[0].id._id) ||
      currentCompanyId);

  const resetTextLength = useCallback(() => {
    setTitleLength(initialValues.title.length);
    setDescriptionLength(initialValues.description.length);
  }, [initialValues.description.length, initialValues.title.length]);

  const computedInitialState = {
    ...initialValues,
    sports: initialValues.sports.map((sport) => sport._id),
    location: {
      lat: initialValues.location.coordinates[1],
      lng: initialValues.location.coordinates[0],
      label: initialValues.locationName
    }
  };

  const handleUpdateGroupSubmit = useCallback(
    (values: GroupCreateFormState) => {
      const {location, title, description, sports, accessType} = values;
      if (location) {
        const teamObject = {
          createdBy: {_id: userValues._id || null},
          managers: [userValues._id],
          title: title,
          description: description,
          locationName: location.label,
          location: {
            coordinates: [
              location.lng || DEFAULT_LONGITUDE,
              location.lat || DEFAULT_LATITUDE
            ]
          },
          sports: sports,
          accessType: accessType,
          picture: imageDeleted
            ? null
            : selectedFile || computedInitialState.picture,
          company: companyId || undefined,
          trainerEvent: userRole === 'TRAINER' ? true : false,
          tag: allowedTag
        };

        updateGroup(initialValues._id, teamObject)
          .then((response) => {
            if (response) {
              if (onSubmit) {
                onSubmit(response);
              }
            } else {
              throw new Error('Server response is not as expected');
            }
          })
          .catch((error) => {
            console.error('Error:', error);
            if (onError) {
              onError(error as Error);
            }
          });
      }
    },
    [
      companyId,
      computedInitialState.picture,
      imageDeleted,
      initialValues._id,
      onError,
      onSubmit,
      selectedFile,
      userRole,
      userValues,
      allowedTag
    ]
  );

  const handleImageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 0.5) {
          setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'KB'}));

          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onload = () => {
          setSelectedFile(reader.result as string);

          setImageDeleted(false);

          setFileSizeError(null);
        };
      }
    },
    [t]
  );

  const handleDeleteImage = useCallback(() => {
    setFileSizeError(null);
    setImageDeleted(true);
    setSelectedFile(null);
  }, []);

  const pictureWithTimestamp = `${initialValues.picture}?timestamp=${initialValues.updatedAt}`;

  const pictureSource = useMemo(() => {
    return !imageDeleted && (selectedFile || initialValues.picture)
      ? selectedFile || pictureWithTimestamp
      : selectedSports.length === 1
      ? sportsFromRedux.sports[selectedSports[0]]?.picture
      : groupsImage || null;
  }, [
    imageDeleted,
    selectedFile,
    initialValues.picture,
    pictureWithTimestamp,
    selectedSports,
    sportsFromRedux.sports
  ]);

  return (
    <>
      <Formik
        //enableReinitialize={true}
        initialValues={computedInitialState}
        validationSchema={groupEditSchema}
        onSubmit={(values) => {
          handleUpdateGroupSubmit(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          resetForm,
          setFieldValue,
          setFieldTouched
        }) => {
          //console.log(errors);
          return (
            <Form>
              <TypographyTitle textKey="update_group" />
              {trainerInSjOne &&
                availableCompanies &&
                availableCompanies.length > 0 && (
                  <Grid container spacing={2} marginTop={1} marginBottom={3}>
                    <Grid item xs={12}>
                      <CompanyInput
                        companies={availableCompanies}
                        value={
                          availableCompanies.find(
                            (company) => company.id._id === values.company
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          if (newValue && !Array.isArray(newValue)) {
                            setFieldValue('company', newValue.id._id);
                          } else {
                            setFieldValue('company', undefined);
                          }
                        }}
                        error={errors.company}
                        touched={!!touched.company}
                        onBlur={handleBlur}
                        label={t('company')}
                        disabled
                      />
                    </Grid>
                  </Grid>
                )}
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    label={t('event_prop_title')}
                    value={values.title}
                    onChange={(e) => {
                      handleChange(e);
                      setTitleLength(e.target.value.length);
                    }}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    fullWidth
                  />
                  <FormHelperText>
                    {titleLength} / {TITLE_MAX_LENGTH}{' '}
                  </FormHelperText>
                </Grid>
              </Grid>
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label={t('description')}
                    value={values.description}
                    onChange={(e) => {
                      handleChange(e);
                      setDescriptionLength(e.target.value.length);
                    }}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    multiline
                    maxRows={4}
                  />

                  <FormHelperText>
                    {descriptionLength} / {DESC_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <LocationInput
                    name="location"
                    label={t('location')}
                    value={values.location}
                    error={errors['location']}
                    touched={touched['location']}
                    // error={
                    //   errors['location']
                    //     ? Object.values(errors.location)[0]
                    //     : undefined
                    // }
                    // touched={
                    //   touched['location']
                    //     ? Object.values(touched.location).some(Boolean)
                    //     : undefined
                    // }
                    onBlur={() => {
                      setFieldTouched('location', true);
                    }}
                    onChange={(location) => {
                      setFieldValue('location', location);
                    }}
                    initialLocation={
                      computedInitialState.location || userValues?.location
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <SportSelectInput
                    name="sports"
                    optional
                    value={values.sports}
                    error={errors.sports}
                    touched={touched.sports}
                    onBlur={handleBlur}
                    onChange={(sportIds) => {
                      setFieldValue('sports', sportIds);
                      setSelectedSports(sportIds as string[]);
                    }}
                    multiple={true}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} my={3} justifyContent={'center'}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.accessType === 'PRIVATE'}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? 'PRIVATE'
                            : 'PUBLIC';
                          setFieldValue('accessType', newValue);
                        }}
                        name="accessType"
                      />
                    }
                    label={t('group_private')}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                marginBottom={3}
                marginTop={{xs: 0, sm: 3}}
                justifyContent={'center'}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<CachedIcon />}
                    onClick={handleDeleteImage}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('show_default_photo')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <label htmlFor="photo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('upload_photo')}
                    </Button>
                  </label>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  <label htmlFor="phote-delete-xs">
                    <IconButton
                      aria-label={t('show_default_photo')}
                      color="error"
                      onClick={handleDeleteImage}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <CachedIcon />
                    </IconButton>
                  </label>

                  <input
                    id="photo-upload"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                  <label htmlFor="photo-upload-xs">
                    <IconButton
                      aria-label={t('upload_photo')}
                      component="span"
                      color="secondary"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '1px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="photo-upload-xs"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: {xs: 0, sm: 1}
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 2,
                      flexDirection: 'column'
                    }}
                  >
                    <ResponsiveTypography>
                      {t('group_photo')}
                    </ResponsiveTypography>
                    <ResponsiveTypography>
                      {t('optimal_dimensions', {width: 2, height: 1})}
                    </ResponsiveTypography>
                  </Box>

                  <Box
                    component="img"
                    src={pictureSource as string}
                    alt={t('group_image')}
                    sx={{
                      // marginRight: '10px',
                      objectFit: 'cover',
                      width: {xs: '240px', sm: '300px'},
                      height: {xs: '120px', sm: '150px'},

                      // maxWidth: { xs: '240px', sm: '300px' },
                      // maxHeight: { xs: '120px', sm: '150px' },
                      marginTop: {xs: 2, sm: 0}
                    }}
                  />
                </Grid>
              </Grid>

              {fileSizeError && (
                <ResponsiveTypography
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    color: 'red',
                    marginTop: 3
                  }}
                >
                  {fileSizeError}
                </ResponsiveTypography>
              )}

              <Grid container spacing={2} my={1} marginBottom={{xs: 0, sm: 4}}>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: {xs: 3, sm: 0},
                    order: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetForm();
                      if (onCancel) {
                        onCancel();
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetForm();
                      resetTextLength();
                      setSelectedFile(null);

                      setFileSizeError(null);
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('reset')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: {xs: 1, sm: 0},
                    order: {xs: -1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!!fileSizeError}
                    onClick={() => handleSubmit()}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('update')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default GroupEditForm;
